/* NEEDS YOU, the time rail and the map insets (docs/design/inbox.md §8, R1846).
   Every rule reads the tokens of gen/tokens.css and stands under #ui.

   The geometry of the rail on the sheet of a telephone (369 px): the rail
   column is 56 px (the hour stamp in 1-31, the tick in 32-36, the circle
   in 37-57 on the hairline at 47), 6 px of gap, then the item: its edge,
   6 px of padding, the two lines, 8 px, and the 56 px inset. The lines
   keep about 229 px, and every line ends in an ellipsis, never a wrap. */

@import '../../gen/tokens.css';

#ui .ny {
  display: flex;
  flex-direction: column;
  margin: 0 0 var(--s-3);
}

/* The head: one line of the mouse target, of the finger target under a
   coarse pointer. Folded, the group is this line alone. */
#ui .ny-head {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  width: 100%;
  min-height: var(--target-mouse);
  margin: 0;
  padding: 0 var(--s-1);
  background: transparent;
  border: 0;
  color: var(--accent);
  font: var(--t-cap);
  letter-spacing: var(--t-cap-track);
  text-transform: uppercase;
  text-align: left;
  cursor: pointer;
}
#ui .ny-hw { flex: none; white-space: nowrap; }
#ui .ny-rule { flex: 1; height: 1px; background: rgb(var(--accent-rgb) / 0.18); }
/* The fold mark wears the square press of the chrome (R1857, R1876). */
#ui .ny-chev { flex: none; display: inline-grid; }

#ui .ny-rail { list-style: none; margin: 0; padding: 0; }

/* A day break: the word, then a hairline. The rail line stops at it. */
#ui .ny-day {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  height: var(--s-6);
  padding-left: var(--s-1);
  color: var(--dim);
  font: var(--t-cap);
  letter-spacing: var(--t-cap-track);
  white-space: nowrap;
}
#ui .ny-day::after { content: ""; flex: 1; height: 1px; background: rgb(var(--accent-rgb) / 0.1); }

/* One item and its rail column. The hairline runs down the column through
   the centre of the circle, from item to item. */
#ui .ny-e {
  position: relative;
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  column-gap: 6px;
  padding-bottom: var(--s-2);
}
#ui .ny-e::before {
  content: "";
  position: absolute;
  left: 47px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgb(var(--accent-rgb) / 0.28);
}
#ui .ny-r { position: relative; }
/* The hour stamp stands 1 px in, clear of the unread mark (record.css). */
/* The hour tick: the stamp, and a short stroke to the line. */
#ui .ny-t {
  position: absolute;
  left: 2px;
  top: 8px;
  width: 30px;
  text-align: right;
  color: var(--dim);
  font: var(--t-cap);
  line-height: 20px;
  font-variant-numeric: tabular-nums;
}
#ui .ny-t::after {
  content: "";
  position: absolute;
  left: 30px;
  top: 10px;
  width: 4px;
  height: 1px;
  background: rgb(var(--accent-rgb) / 0.4);
}
/* The numbered circle of an item with a cell: the number of its pin. */
#ui .ny-n {
  position: absolute;
  left: 37px;
  top: 8px;
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  box-sizing: border-box;
  border: 1px solid var(--accent);
  border-radius: 50%;
  background: rgb(var(--sheet-rgb));
  color: var(--hero-ink);
  font: var(--t-cap);
  font-variant-numeric: tabular-nums;
}
#ui .ny-bad .ny-n { border-color: var(--bad); }
/* The mark of an item with no cell: no number, no pin. */
#ui .ny-m {
  position: absolute;
  left: 43px;
  top: 14px;
  width: 8px;
  height: 8px;
  border-radius: 1px;
  background: var(--dim);
}
#ui .ny-bad .ny-m { background: var(--bad); }

/* The item: the open press over the lines and the inset, then the
   presses of the message. */
#ui .ny-i {
  display: flex;
  flex-direction: column;
  gap: var(--s-2);
  min-width: 0;
  padding: 6px;
  border: 1px solid rgb(var(--accent-rgb) / 0.12);
  border-radius: var(--r-button);
  background: rgb(var(--panel-rgb) / 0.5);
}
#ui .ny-new .ny-i { border-color: rgb(var(--accent-rgb) / 0.28); }
/* The lit item (the pointed, the focused or the last pressed one): its
   circle takes a second ring, as its pin on the map does. */
#ui .ny-lit .ny-i, #ui .ny-e:focus-within .ny-i { border-color: rgb(var(--accent-rgb) / 0.5); }
#ui .ny-lit .ny-n, #ui .ny-e:focus-within .ny-n { outline: 1px solid var(--accent); outline-offset: 2px; }
#ui .ny-bad.ny-lit .ny-n, #ui .ny-bad:focus-within .ny-n { outline-color: var(--bad); }
#ui .ny-open {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: var(--s-3);
  align-items: start;
  width: 100%;
  margin: 0;
  padding: 0;
  background: transparent;
  border: 0;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
#ui .ny-ln { display: flex; flex-direction: column; min-width: 0; }
#ui .ny-ln > * { min-width: 0; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
#ui .ny-ins { display: block; line-height: 0; }
#ui .ny-acts { display: flex; gap: var(--s-2); min-width: 0; overflow: hidden; }

/* The map inset: the water, the contours as hex edges, the pin and the
   place name on a dark stroke, so a contour never runs through a letter. */
#ui .ny-map { display: block; border-radius: var(--r-bar); }
#ui .ny-map .ny-w { fill: rgb(var(--deep-rgb)); }
#ui .ny-map .ny-c1 { fill: none; stroke: var(--accent); stroke-opacity: 0.55; stroke-width: 1; stroke-linecap: round; }
#ui .ny-map .ny-c2 { fill: none; stroke: var(--accent); stroke-opacity: 0.28; stroke-width: 0.8; stroke-linecap: round; }
#ui .ny-map .ny-tr { fill: none; stroke: var(--dim); stroke-opacity: 0.7; stroke-width: 0.8; }
#ui .ny-map .ny-pin { fill: rgb(var(--sheet-rgb)); stroke: var(--accent); stroke-width: 1.2; }
#ui .ny-bad .ny-map .ny-pin { stroke: var(--bad); }
#ui .ny-map text { font: var(--t-cap); text-anchor: middle; fill: var(--text); }
#ui .ny-map .ny-pn { fill: var(--hero-ink); dominant-baseline: central; font-variant-numeric: tabular-nums; }
#ui .ny-map .ny-pl {
  letter-spacing: 0;
  paint-order: stroke;
  stroke: rgb(var(--deep-rgb));
  stroke-width: 3px;
  stroke-linejoin: round;
}

@media (pointer: coarse) {
  #ui .ny-head { min-height: var(--target-finger); }
}

@media (hover: hover) {
  #ui .ny-head:hover { color: var(--hover-ink); }
  #ui .ny-open:hover .ny-map { outline: 1px solid rgb(var(--accent-rgb) / 0.4); }
  #ui .ny-i:hover { border-color: rgb(var(--accent-rgb) / 0.36); }
  #ui .ny-e:hover .ny-n { outline: 1px solid var(--accent); outline-offset: 2px; }
  #ui .ny-bad:hover .ny-n { outline-color: var(--bad); }
}
